import React, { useState } from "react";
import { ModalForm } from "collections/Modal";
import { DISMISS, FieldProps, Form, useNotification } from "collections";
import {
  generatePath,
  Link,
  Route,
  useRouteMatch,
  useHistory
} from "polyrouter";
import { Field } from "collections/Form";
import { SubmitButton } from "elements";
import { LocalKeys, useTranslation } from "utilities/i18n";
import { InferType, object, string } from "yup";
import {
  useMutateUserGroupMembershipMutation,
  useSaveUserGroupMutation
} from "generated/graphql";
import { GroupDropdown } from "./GroupDropdown";
import { GroupInput } from "./GroupInput";

export type GroupMutationData = {
  name: string;
  id: string;
};

export const UserGroupSchema = object({
  name: string().label("Group Name"),
  id: string().label("Group Id")
});
export type UserGroupType = InferType<typeof UserGroupSchema>;

export const UserGroupMutationForm: React.FC<{
  tenantId: string;
  urlEndPath: string;
  deviceUsers: Array<{ name: string; id: string }>;
  title: LocalKeys;
  description: LocalKeys;
  mutationResultKey: LocalKeys;
  GroupInput?: typeof GroupInput;
  GroupDropdown?: typeof GroupDropdown;
  initialGroupData?: GroupMutationData;
  operation: "Add" | "Remove" | "Create";
  successMessage: LocalKeys;
  showLinkOnSuccess: boolean;
  errorMessage: LocalKeys;
}> = ({
  tenantId,
  deviceUsers,
  urlEndPath,
  title,
  description,
  mutationResultKey,
  initialGroupData,
  GroupInput,
  GroupDropdown,
  operation,
  successMessage,
  showLinkOnSuccess,
  errorMessage
}) => {
  const { t } = useTranslation();
  const root = useRouteMatch().path;
  const history = useHistory();
  const [, updateMembership] = useMutateUserGroupMembershipMutation();
  const [, createUserGroup] = useSaveUserGroupMutation();
  const [addNotification] = useNotification();

  const initialValues = {
    name: initialGroupData?.name ?? "",
    id: initialGroupData?.id ?? ""
  };

  const [groupData, setGroupData] = useState<GroupMutationData>(initialValues);

  const { url } = useRouteMatch();
  const cancelURL = url.replace(urlEndPath, "");

  const submit = async () => {
    const users = deviceUsers.map(user => user.id);
    let responseError,
      responseGroupId = undefined;
    if (operation === "Create") {
      const { data, error } = await createUserGroup({
        tenantId: tenantId,
        body: {
          name: groupData.name,
          users: users
        }
      });
      responseError = error;
      responseGroupId = data?.saveUserGroup.id;
    }
    if (["Add", "Remove"].includes(operation)) {
      const { data, error } = await updateMembership({
        tenantId,
        groupId: groupData.id,
        newUsers: operation === "Add" ? users : [],
        removedUsers: operation === "Remove" ? users : []
      });
      responseError = error;
      responseGroupId = data?.mutateUserGroupMembership.id;
    }

    // we check for id here as well to avoid backend bugs causing client side crashes,
    // In the case where data is null but no error is thrown.
    if (!responseError && responseGroupId) {
      const messageContent = t(successMessage, { count: users.length });
      const message = (
        <span className="ml-14">
          {messageContent}
          {showLinkOnSuccess && (
            <Link
              className="font-bold ml-14"
              to={generatePath("/manage/device-users/groups/details/:id", {
                id: responseGroupId
              })}
            >
              {t("VIEW_GROUP")}
            </Link>
          )}
        </span>
      );

      addNotification({
        type: "success",
        dismiss: DISMISS.DEFAULT,
        message,
        persistThroughNav: true
      });
      return;
    }
    addNotification({
      type: "error",
      dismiss: DISMISS.DEFAULT,
      message: t(errorMessage, { message: responseError?.message }),
      persistThroughNav: true
    });
  };

  return (
    <ModalForm
      title={t(title)}
      initialValues={initialValues}
      onSubmit={() => {
        submit();
        history.push(cancelURL);
      }}
      enableReinitialize
      name={"UserGroupMutationModal"}
      validationSchema={UserGroupSchema}
    >
      {() => (
        <Form>
          <Route exact path={root}>
            <span>{t(description)}</span>
            {GroupInput && (
              <GroupInput groupData={groupData} setGroupData={setGroupData} />
            )}
            {GroupDropdown && (
              <GroupDropdown tenantId={tenantId} setGroupData={setGroupData} />
            )}
            <div className="btn-container">
              {groupData !== undefined ? (
                <>
                  <Link to={cancelURL} className="btn">
                    {t("CANCEL")}
                  </Link>
                  <Link
                    nest
                    to="/confirm"
                    disabled={!groupData?.name}
                    className={`btn btn-primary${
                      !groupData?.name ? " disabled" : ""
                    }`}
                  >
                    {t("CONTINUE")}
                  </Link>
                </>
              ) : (
                <Link to={cancelURL} className="btn btn-primary">
                  {t("OK")}
                </Link>
              )}
            </div>
          </Route>
          <Route nest path="/confirm">
            <div className="form-content">
              <span>
                {t(mutationResultKey, {
                  count: deviceUsers.length,
                  name: groupData?.name
                })}
              </span>
            </div>
            <div className="btn-container">
              <Link
                to={`${cancelURL}${urlEndPath}`}
                className="btn btn-secondary"
              >
                {t("BACK")}
              </Link>
              <Link to={cancelURL} className="btn">
                {t("CANCEL")}
              </Link>
              <Field name="submit">
                {({ form: { isSubmitting } }: FieldProps) => (
                  <SubmitButton
                    isSubmitting={isSubmitting}
                    labelKey="CONFIRM"
                    submittingLabelKey="SAVING"
                  />
                )}
              </Field>
            </div>
          </Route>
        </Form>
      )}
    </ModalForm>
  );
};
